import type { Meta, StoryObj } from '@storybook/angular';
import { signal, computed } from '@angular/core';
import { expect, within } from 'storybook/test';
import { ProductWidgetComponent } from '../../../app/content/layout/components/product/product-widget/product-widget.component';
import { ProductService } from '../../../app/core/services/product.service';
import { ProductPriceService } from '../../../app/core/services/product-price.service';
import { withCommonDecorators } from '../../_utils/story-helpers';
import {
  createDamilanoBarberaProduct,
  createDiscountedProduct,
  createQuantityPricedProduct,
  createProductWithRatings,
  createProductWithoutRatings,
  createOutOfStockProduct,
  createPreorderProduct,
  createCustomBadgeProduct,
  createLastBottlesProduct,
  createDiscountCountdownProduct,
} from '../../_mocks';
import { PriceLine } from '../../../app/content/layout/components/product/price-table/price-table.component';

const defaultPriceLines: PriceLine[] = [
  { isDiscounted: false, label: '1 fl.', price: 8900, currencyCode: 'DKK', showVatText: true, qty: 1, unitName: 'fl.' },
];

function productProviders(priceLines: PriceLine[] = defaultPriceLines) {
  return [
    {
      provide: ProductService,
      useValue: {
        getFormattedProductPricesSignal: () => signal(priceLines),
        getDefaultPriceSignal: () => computed(() => priceLines[0]?.price),
        getDefaultPriceFromProduct: () => priceLines[0]?.price ?? 0,
      },
    },
    {
      provide: ProductPriceService,
      useValue: {
        currencyCode: signal('DKK'),
        getUnitName: () => 'fl.',
        calculateDiscountPercentage: () => 0,
        formatPriceLines: () => priceLines,
      },
    },
  ];
}

const meta: Meta<ProductWidgetComponent> = {
  title: 'Organisms/Product/Product Widget',
  component: ProductWidgetComponent,
  parameters: {
    layout: 'centered',
    docs: {
      description: {
        component: 'Product card widget used in listings, carousels, and search results. Displays image, title, ratings, price table, tags, and add-to-cart.',
      },
    },
  },
};

export default meta;
type Story = StoryObj<ProductWidgetComponent>;

export const Default: Story = {
  decorators: [...withCommonDecorators(productProviders())],
  args: {
    product: createDamilanoBarberaProduct() as any,
    index: 0,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/damilano barbera/i)).toBeInTheDocument();
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};

export const WithDiscount: Story = {
  decorators: [...withCommonDecorators(productProviders([
    { isDiscounted: true, isStriked: true, label: '1 fl.', price: 8900, currencyCode: 'DKK', qty: 1, unitName: 'fl.' },
    { isDiscounted: true, prefix: ['Tilbudspris'], label: '1 fl.', price: 6900, currencyCode: 'DKK', showVatText: true, qty: 1, unitName: 'fl.' },
  ]))],
  args: {
    product: createDiscountedProduct() as any,
    index: 1,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};

export const VolumePricing: Story = {
  decorators: [...withCommonDecorators(productProviders([
    { isDiscounted: false, label: '1-5 fl.', price: 8900, currencyCode: 'DKK', showVatText: true, qty: 1, unitName: 'fl.' },
    { isDiscounted: true, label: '6-11 fl.', price: 7900, currencyCode: 'DKK', qty: 6, unitName: 'fl.' },
    { isDiscounted: true, label: '12+ fl.', price: 6900, currencyCode: 'DKK', qty: 12, unitName: 'fl.' },
  ]))],
  args: {
    product: createQuantityPricedProduct() as any,
    index: 2,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};

export const WithRatings: Story = {
  decorators: [...withCommonDecorators(productProviders())],
  args: {
    product: createProductWithRatings() as any,
    index: 3,
    eventSourceName: 'storybook',
    showCustomerRating: true,
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};

export const NoRatings: Story = {
  decorators: [...withCommonDecorators(productProviders())],
  args: {
    product: createProductWithoutRatings() as any,
    index: 4,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};

export const OutOfStock: Story = {
  decorators: [...withCommonDecorators(productProviders())],
  args: {
    product: createOutOfStockProduct() as any,
    index: 5,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};

export const CustomBadge: Story = {
  decorators: [...withCommonDecorators(productProviders())],
  args: {
    product: createCustomBadgeProduct() as any,
    index: 6,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};

export const Preorder: Story = {
  decorators: [...withCommonDecorators(productProviders())],
  args: {
    product: createPreorderProduct() as any,
    index: 7,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};

export const HideDefaultCta: Story = {
  decorators: [...withCommonDecorators(productProviders())],
  args: {
    product: createDamilanoBarberaProduct() as any,
    index: 8,
    eventSourceName: 'storybook',
    hideProductDefaultCta: true,
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};

export const LastBottles: Story = {
  decorators: [...withCommonDecorators(productProviders([
    { isDiscounted: true, isStriked: true, label: '1 fl.', price: 10995, currencyCode: 'DKK', qty: 1, unitName: 'fl.' },
    { isDiscounted: true, prefix: ['Tilbudspris'], label: '1 fl.', price: 8995, currencyCode: 'DKK', showVatText: true, qty: 1, unitName: 'fl.' },
  ]))],
  args: {
    product: createLastBottlesProduct() as any,
    index: 9,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/altos da beira/i)).toBeInTheDocument();
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};

export const DiscountCountdown: Story = {
  decorators: [...withCommonDecorators(productProviders([
    { isDiscounted: true, isStriked: true, label: '1 ks.', price: 156970, currencyCode: 'DKK', qty: 1, unitName: 'ks.' },
    { isDiscounted: true, prefix: ['Tilbudspris'], label: '1 ks.', price: 92995, currencyCode: 'DKK', showVatText: true, qty: 1, unitName: 'ks.' },
  ]))],
  args: {
    product: createDiscountCountdownProduct() as any,
    index: 10,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/tesch riesling/i)).toBeInTheDocument();
    await expect(canvasElement.querySelector('.product-widget-description')).toBeTruthy();
  },
};
